/**
 * Opponent Dice Manager
 *
 * Manages dice for other players:
 * 1. When opponent joins: Create ALL dice upfront (3 for Cee-Lo) - ONCE
 * 2. Activate/deactivate dice based on game phase (HI_LO=1, BANKER_ROLL=3)
 * 3. Never destroy dice until opponent leaves
 *
 * Uses real Dice objects with Cannon-ES physics for accurate simulation.
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { DiceSyncPayload, DiceThrowParams, DicePhase } from '@sdl/shared';
import { Engine } from '../../engine/Engine';
import { Dice } from './Dice';
import { sound, SoundId } from '../../engine/audio/sound';
import { TSS } from '../../tss/TSS';
import type { UINodeConfig } from '../../tss/types';
import type { DiceAssetManager, DiceAsset } from './assets/DiceAssetManager';
import { OpponentDragConfig } from '../config/OpponentDragConfig';
import { renderScheduler } from '../../engine/render/RenderScheduler';
import { prewarmShaders } from '../../engine/render/precompile';

/** Max dice per opponent (Cee-Lo uses 3) */
const MAX_DICE_COUNT = 3;
const _worldPosition = new THREE.Vector3();

/** Player info for display labels */
export interface PlayerInfo {
  username: string;
  avatar?: string;
  equippedDice?: string[];
}

interface OpponentDie {
  dice: Dice;
  active: boolean;  // true = in scene, false = not in scene
  asset: DiceAsset | null;
}

/**
 * One historical drag snapshot kept in the per-opponent interpolation buffer.
 * `time` is the LOCAL arrival timestamp (Date.now()) — using arrival time
 * instead of server timestamp avoids client/server clock-skew problems.
 */
interface DragSnapshot {
  position: { x: number; y: number; z: number };
  time: number;
}

interface OpponentState {
  playerId: string;
  playerInfo: PlayerInfo;         // Username and avatar for display
  dice: OpponentDie[];            // All dice for this opponent (always MAX_DICE_COUNT)
  heldDice: Dice[];               // Dice currently being dragged
  activeDiceCount: number;        // How many dice are active for current phase
  targetPosition: THREE.Vector3;
  targetRotation: THREE.Quaternion;
  phase: DicePhase;               // Server-authoritative dice phase
  lastDragSeq: number;
  lastDragTimestamp: number;
  /** Ring of recent drag snapshots, oldest first. The update loop interpolates
   *  between buffer[i] and buffer[i+1] at renderTime = now - INTERPOLATION_DELAY_MS. */
  dragBuffer: DragSnapshot[];
  dragGroup: THREE.Group | null;  // Group that holds dragging dice + label (single lerp target)
  dragLabel: TSS | null; // Billboard label shown during dragging
  resultLabel: TSS | null; // Billboard label shown above stopped dice during HI_LO
  readyLabel: TSS | null; // Billboard label shown above resting dice before opponent drags
  equippedDice: string[];         // Opponent's equipped dice IDs for asset loading
}

export class OpponentDiceManager {
  private engine: Engine;
  private opponents: Map<string, OpponentState> = new Map();
  private localPlayerId: string | null = null;
  private assetManager: DiceAssetManager | null = null;

  /** In-flight applyFinalStates correction tweens, keyed by die. Tracked so a
   *  new throw (or a fresh correction) can kill a stale tween — its onUpdate
   *  writes mesh AND body pose, which would corrupt the next replay's physics
   *  if it outlived the transition into the new flight. */
  private finalStateTweens: Map<Dice, gsap.core.Tween> = new Map();

  /** Kill (and forget) a die's pending final-state correction tween, if any. */
  private killFinalStateTween(die: Dice): void {
    const tween = this.finalStateTweens.get(die);
    if (tween) {
      tween.kill();
      this.finalStateTweens.delete(die);
    }
  }

  // Interpolation settings for smooth drag following
  /** Client-side interpolation delay for opponent drag. Render the dragGroup
   *  at "now - INTERPOLATION_DELAY_MS" by interpolating between buffered
   *  snapshots. 100ms gives ~3 buffered packets at the 30Hz server rate —
   *  enough headroom that one delayed/dropped packet doesn't cause stalls.
   *  This trades 100ms of "display lag" for mathematically smooth motion
   *  (no exponential lerp easing, no jitter from packet timing variance).
   *  Canonical FPS-netcode pattern (Source/Quake/Unity entity interpolation).
   */
  private readonly HOLD_SPACING = 1.2;

  // ── Avatar images for 3D labels (shared cache, keyed by URL) ──
  /** Avatar URLs load into HTMLImageElements; the TSS circle draws the image
   *  (never the raw URL — that was the "filename shows instead of photo" bug). */
  private static avatarImages = new Map<string, HTMLImageElement>();
  private static avatarLoadListeners = new Set<() => void>();

  private static loadAvatar(url: string): HTMLImageElement {
    let img = OpponentDiceManager.avatarImages.get(url);
    if (img) return img;
    img = new Image();
    // CORS is REQUIRED — the image is drawn to a canvas that becomes a WebGL
    // texture. A cross-origin image without CORS taints the canvas and the
    // texture upload throws (SecurityError), breaking the whole label. With
    // this, the canvas stays clean (CDN must send Access-Control-Allow-Origin).
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      for (const l of OpponentDiceManager.avatarLoadListeners) l();
    };
    // If CORS/load fails, naturalWidth stays 0 → avatarCircle falls back to the
    // initial, so the label still renders (just without the photo).
    img.onerror = () => { /* keep initial fallback */ };
    img.src = url;
    OpponentDiceManager.avatarImages.set(url, img);
    // Warm cache with a hard cap — one decoded image per distinct opponent
    // avatar URL would otherwise grow for the whole app session. Insertion
    // order eviction; an evicted avatar transparently re-fetches on next use.
    if (OpponentDiceManager.avatarImages.size > 32) {
      const oldest = OpponentDiceManager.avatarImages.keys().next().value;
      if (oldest !== undefined) OpponentDiceManager.avatarImages.delete(oldest);
    }
    return img;
  }

  /**
   * Resolve a player's avatar to a TSS circle's image/text. A URL renders as a
   * loaded image; anything else (emoji, empty) falls back to the username
   * initial. NEVER returns the raw URL as text.
   */
  private avatarCircle(info: PlayerInfo): { image?: HTMLImageElement; text?: string } {
    const raw = (info.avatar ?? '').trim();
    const initial = info.username.trim().charAt(0).toUpperCase() || '?';
    const isUrl = /^https?:\/\//i.test(raw) || raw.startsWith('/');
    if (!isUrl) return { text: raw || initial };
    const img = OpponentDiceManager.loadAvatar(raw);
    if (img.complete && img.naturalWidth > 0) return { image: img };
    return { text: initial }; // still loading — show initial, redraw on load
  }

  /** Start loading a player's avatar URL (if any) into the shared cache. */
  private preloadAvatar(info?: PlayerInfo): void {
    const raw = (info?.avatar ?? '').trim();
    if (/^https?:\/\//i.test(raw) || raw.startsWith('/')) OpponentDiceManager.loadAvatar(raw);
  }

  /** Rebuild every live label so a just-loaded avatar image gets picked up. */
  private redrawLabels = (): void => {
    for (const state of this.opponents.values()) {
      if (state.dragLabel) { state.dragLabel.setTree(this.dragLabelTree(state.playerInfo)); state.dragLabel.render(); }
      if (state.readyLabel) { state.readyLabel.setTree(this.dragLabelTree(state.playerInfo)); state.readyLabel.render(); }
      if (state.resultLabel) { state.resultLabel.setTree(this.resultLabelTree(state.playerInfo)); state.resultLabel.render(); }
    }
  };

  constructor(engine: Engine) {
    this.engine = engine;
    OpponentDiceManager.avatarLoadListeners.add(this.redrawLabels);
  }

  setLocalPlayerId(playerId: string | null): void {
    this.localPlayerId = playerId;
  }

  setAssetManager(manager: DiceAssetManager | null): void {
    this.assetManager = manager;
  }

  /**
   * Update opponent's dice phase from server broadcast
   * Server is the source of truth for when dice should be visible
   */
  setOpponentPhase(playerId: string, phase: DicePhase): void {
    const state = this.opponents.get(playerId);
    if (state) {
      state.phase = phase;
    }
  }

  /**
   * Set how many dice should be active for this opponent
   * Call this when game phase changes (HI_LO=1, BANKER_ROLL=3)
   */
  setActiveDiceCount(playerId: string, count: number): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);
    if (!state) {
      // Auto-load if not exists
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    const newCount = Math.min(count, MAX_DICE_COUNT);
    if (state.activeDiceCount !== newCount) {
      state.activeDiceCount = newCount;
    }
  }

  /**
   * Ensure opponent has correct active dice count for current game state
   * Alias for setActiveDiceCount for backward compatibility
   */
  ensureOpponentDiceCount(playerId: string, expectedCount: number): void {
    this.setActiveDiceCount(playerId, expectedCount);
  }

  /**
   * Get opponent's equipped dice IDs
   */
  getOpponentEquippedDice(playerId: string): string[] {
    return this.opponents.get(playerId)?.equippedDice ?? [];
  }

  /**
   * Get opponent's current dice phase
   */
  getOpponentPhase(playerId: string): DicePhase {
    return this.opponents.get(playerId)?.phase ?? DicePhase.IDLE;
  }

  /**
   * Get opponent's active dice (for camera tracking)
   */
  getOpponentActiveDice(playerId: string): Dice[] {
    const state = this.opponents.get(playerId);
    if (!state) return [];

    return state.dice
      .slice(0, state.activeDiceCount)
      .filter((die) => die.active)
      .map((die) => die.dice);
  }

  /**
   * Get active dice from all opponents whose phase is STOPPED (have a result).
   * Used during HI_LO to frame all result dice in the camera.
   */
  getAllStoppedDice(): Dice[] {
    const result: Dice[] = [];
    for (const state of this.opponents.values()) {
      if (state.phase !== DicePhase.STOPPED) continue;
      for (let i = 0; i < state.activeDiceCount; i++) {
        if (state.dice[i]?.active) {
          result.push(state.dice[i].dice);
        }
      }
    }
    return result;
  }

  /** Get ALL active dice across all opponents (any phase) */
  getAllActiveDice(): Dice[] {
    const result: Dice[] = [];
    for (const state of this.opponents.values()) {
      for (let i = 0; i < state.activeDiceCount; i++) {
        if (state.dice[i]?.active) {
          result.push(state.dice[i].dice);
        }
      }
    }
    return result;
  }

  /**
   * Show a name label floating above an opponent's first active die.
   * Used during HI_LO to identify whose die is whose.
   */
  /** Result label tree (avatar circle + name) shown above stopped HI_LO dice. */
  private resultLabelTree(info: PlayerInfo): UINodeConfig {
    return {
      tag: 'root',
      style: {
        direction: 'row',
        alignItems: 'center',
        padding: [8, 12, 8, 8],
        gap: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderRadius: 10,
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
      },
      children: [
        {
          tag: 'avatar',
          style: { color: '#FFFFFF', fontFamily: 'sans-serif' },
          circle: {
            radius: 18,
            fill: 'rgba(255, 215, 0, 0.15)',
            stroke: 'rgba(255, 215, 0, 0.6)',
            strokeWidth: 2,
            ...this.avatarCircle(info),
            textSize: 20,
          },
        },
        {
          tag: 'name',
          text: info.username,
          style: {
            fontWeight: 'bold',
            fontSize: 18,
            fontFamily: 'Oswald, sans-serif',
            color: '#FFFFFF',
            maxWidth: 160,
            overflow: 'ellipsis',
          },
        },
      ],
    };
  }

  showResultLabel(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state) return;

    // Find the first active die to position above
    const activeDie = state.dice.find((d, i) => d.active && i < state.activeDiceCount);
    if (!activeDie) return;

    // Reuse existing label if present
    if (state.resultLabel) {
      const pos = activeDie.dice.getPosition();
      state.resultLabel.object3D.position.set(pos.x, pos.y + 1.5, pos.z);
      state.resultLabel.object3D.visible = true;
      return;
    }

    const info = state.playerInfo;
    if (!info) return;

    const label = new TSS(
      this.resultLabelTree(info),
      {
        worldHeight: 0.5,
        mode: 'sprite',
        // 2× DPI so the name text stays crisp on retina (the canvas is upscaled
        // onto the sprite; at res 1 it blurred). Tiny label → negligible VRAM.
        resolution: 2,
        depthTest: false,
        depthWrite: false,
      }
    );

    label.render();
    const pos = activeDie.dice.getPosition();
    label.object3D.position.set(pos.x, pos.y + 1.5, pos.z);
    this.engine.scene.add(label.object3D);

    state.resultLabel = label;
  }

  /** Hide and dispose result label for an opponent */
  hideResultLabel(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state?.resultLabel) return;
    state.resultLabel.object3D.removeFromParent();
    state.resultLabel.dispose();
    state.resultLabel = null;
  }

  /** Hide all result labels */
  hideAllResultLabels(): void {
    for (const [playerId] of this.opponents) {
      this.hideResultLabel(playerId);
    }
  }

  /**
   * Apply phases from server state (for reconnection)
   */
  applyPlayerRollStates(playerRollStates: Record<string, { phase: DicePhase; throwParams: unknown; result: unknown }>): void {
    for (const [playerId, rollState] of Object.entries(playerRollStates)) {
      if (playerId === this.localPlayerId) continue;

      const state = this.opponents.get(playerId);
      if (state) {
        state.phase = rollState.phase;
        if ((rollState as any).diceCount !== undefined) {
          state.activeDiceCount = Math.min((rollState as any).diceCount ?? MAX_DICE_COUNT, MAX_DICE_COUNT);
        }
      }
    }
  }

  /**
   * Hydrate drag pose from snapshot (reconnection/late join)
   */
  hydrateDragSnapshot(playerId: string, dragPose: { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number; w: number }; seq: number; timestamp: number }, diceCount: number): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);
    if (!state) {
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    state.activeDiceCount = Math.min(diceCount, MAX_DICE_COUNT);
    state.phase = DicePhase.COLLECTING;
    state.lastDragSeq = dragPose.seq;
    state.lastDragTimestamp = Date.now(); // local clock — see handleDiceSync note
    state.targetPosition.set(dragPose.position.x, dragPose.position.y, dragPose.position.z);
    state.targetRotation.set(dragPose.rotation.x, dragPose.rotation.y, dragPose.rotation.z, dragPose.rotation.w);

    // Activate and pick up dice for immediate visibility
    state.heldDice = [];
    for (let i = 0; i < state.activeDiceCount && i < state.dice.length; i++) {
      const opponentDie = state.dice[i];
      if (!opponentDie.active) {
        this.engine.scene.add(opponentDie.dice.mesh);
        opponentDie.active = true;
      }
      opponentDie.dice.pickup();
      const offsetX = (i - (state.activeDiceCount - 1) / 2) * this.HOLD_SPACING;
      opponentDie.dice.setPosition({
        x: dragPose.position.x + offsetX,
        y: dragPose.position.y,
        z: dragPose.position.z,
      });
      opponentDie.dice.mesh.quaternion.set(
        dragPose.rotation.x,
        dragPose.rotation.y,
        dragPose.rotation.z,
        dragPose.rotation.w,
      );
      opponentDie.dice.body.quaternion.set(
        dragPose.rotation.x,
        dragPose.rotation.y,
        dragPose.rotation.z,
        dragPose.rotation.w,
      );
      state.heldDice.push(opponentDie.dice);
    }
  }

  /**
   * Load an opponent - create ALL dice upfront (called once when opponent joins).
   * Tries to load custom dice skins via DiceAssetManager; falls back to red boxes.
   */
  loadOpponent(playerId: string, playerInfo?: PlayerInfo): void {
    // Skip local player
    if (playerId === this.localPlayerId) return;

    // Warm the avatar image cache early so the 3D label shows the photo
    // (not the initial) the first time it appears.
    this.preloadAvatar(playerInfo);

    // Check if already loaded
    if (this.opponents.has(playerId)) {
      // Update player info if provided (for late-join scenarios)
      if (playerInfo) {
        const state = this.opponents.get(playerId)!;
        state.playerInfo = playerInfo;
        // An opponent auto-loaded info-less (dice:phase / drag-sync racing the
        // roster update) was built as red fallbacks with equippedDice=[] —
        // route the real list into the re-skin path now instead of dropping
        // it. Per-slot no-op when the asset already matches; busy phases
        // defer and the loadOpponents hasFallbackSlots poll retries.
        if (playerInfo.equippedDice?.length) {
          this.updateEquippedDice(playerId, playerInfo.equippedDice);
        }
      }
      return;
    }

    const equippedDice = playerInfo?.equippedDice ?? [];

    // Create ALL dice for this opponent (always MAX_DICE_COUNT)
    const dice: OpponentDie[] = [];
    for (let i = 0; i < MAX_DICE_COUNT; i++) {
      const diceId = equippedDice[i] ?? null;
      // The equipped skin if preloaded, else the starter red die (real face
      // map — never show the procedural box for an empty/unloaded slot).
      const asset: DiceAsset | null = this.assetManager?.acquireDiceOrDefault(diceId) ?? null;

      const die = new Dice(this.engine.physics, this.engine.scene, {
        position: { x: 0, y: 4, z: 5 },
        size: 1,
        color: 0xcc0000, // Fallback color (only used if no asset)
      }, asset ?? undefined);
      // Remove from scene - not active yet
      this.engine.scene.remove(die.mesh);
      // Start in held mode (physics disabled)
      die.pickup();
      // Warm this opponent's dice shader at JOIN, not at their first throw. Their
      // skin usually differs from ours, so its program isn't compiled yet, and a
      // mid-roll GLSL compile is the GPU-memory spike that loses the WebGL context
      // on WKWebView (worse the more players are in the room). compileAsync runs
      // off the critical frame and is ready well before they roll; programs dedupe
      // so the 3 dice of one skin compile once. Best-effort, non-blocking.
      if (asset) void prewarmShaders(die.mesh, this.engine.camera, this.engine.scene);
      dice.push({ dice: die, active: false, asset });
    }

    this.opponents.set(playerId, {
      playerId,
      playerInfo: playerInfo ?? { username: 'Player', avatar: '🎲' },
      dice,
      heldDice: [],
      activeDiceCount: MAX_DICE_COUNT, // Default to max, will be adjusted by game state
      targetPosition: new THREE.Vector3(),
      targetRotation: new THREE.Quaternion(),
      phase: DicePhase.IDLE,
      lastDragSeq: -1,
      lastDragTimestamp: 0,
      dragBuffer: [],
      dragGroup: null,
      dragLabel: null,
      resultLabel: null,
      readyLabel: null,
      equippedDice,
    });

    // If any dice still need loading, preload in background and upgrade when
    // ready. One delayed retry on failure (flaky CDN fetch); beyond that,
    // loadOpponents' hasFallbackSlots poll keeps re-attempting each
    // game:update until the skin lands. A slot wearing the default-die
    // stand-in still counts as unloaded — it upgrades to the real skin.
    const unloadedIds = equippedDice.filter((id, i) => id && dice[i].asset?.diceId !== id);
    if (unloadedIds.length > 0 && this.assetManager) {
      this.assetManager.preload(unloadedIds).then(() => {
        this.upgradeOpponentDice(playerId);
      }).catch(() => {
        setTimeout(() => {
          this.assetManager?.preload(unloadedIds).then(() => {
            this.upgradeOpponentDice(playerId);
          }).catch((err) => {
            console.warn('[OpponentDice] background preload failed (after retry)', err);
          });
        }, 2000);
      });
    }
  }

  /**
   * Upgrade opponent dice from fallback red boxes to custom assets after
   * background preload completes. Routes through reskinOpponentSlot so ACTIVE
   * at-rest dice upgrade too (transform preserved, busy phases deferred) —
   * previously an active die kept the red box for the whole phase.
   */
  private upgradeOpponentDice(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state || !this.assetManager) return;

    for (let i = 0; i < state.dice.length; i++) {
      const diceId = state.equippedDice[i] ?? null;
      if (!diceId || state.dice[i].asset?.diceId === diceId) continue;
      this.reskinOpponentSlot(playerId, i, diceId);
    }
  }

  /**
   * True if any equipped slot is still wearing the fallback red box (asset
   * missing at build time or its preload failed). loadOpponents polls this
   * each game:update to re-attempt the skin — without it, a die that was
   * active (or whose download failed) stayed a red box for the whole session
   * even though the equipped list never changed.
   */
  hasFallbackSlots(playerId: string): boolean {
    const state = this.opponents.get(playerId);
    if (!state) return false;
    // "Fallback" = procedural box OR the default-die stand-in — anything not
    // yet wearing the actual equipped skin for a slot that has one.
    return state.dice.some((d, i) => !!state.equippedDice[i] && d.asset?.diceId !== state.equippedDice[i]);
  }

  /**
   * Live equipped-dice change for an already-loaded opponent (in-game swap).
   * Re-skins each INACTIVE slot whose dice code changed; active (in-flight)
   * dice are left alone so a swap can't mutate a die mid-roll. Assets not yet
   * loaded are background-preloaded then applied. Without this, an opponent's
   * 3D dice keep their old/fallback skin even though their profile thumbnails
   * (driven off game:update) already show the new one.
   */
  updateEquippedDice(playerId: string, equippedDice: string[]): void {
    const state = this.opponents.get(playerId);
    if (!state) return;
    state.equippedDice = [...equippedDice];
    if (state.playerInfo) state.playerInfo.equippedDice = [...equippedDice];
    if (!this.assetManager) return;

    // Don't mutate dice while they're being handled (drag) or in physical
    // motion (throw) — they'd teleport / detach from the drag group. They keep
    // the old skin to the end of this beat and re-skin when idle again.
    if (this.isBusyPhase(state.phase)) return;

    for (let i = 0; i < state.dice.length; i++) {
      const newId = equippedDice[i] ?? null;
      const opponentDie = state.dice[i];
      const currentId = opponentDie.asset?.diceId ?? null;
      if (!newId || newId === currentId) continue; // slot unchanged / no target
      if (!this.assetManager.isReady(newId)) {
        const targetId = newId;
        this.assetManager.preload([targetId])
          .then(() => this.reskinOpponentSlot(playerId, i, targetId))
          .catch((err) => console.warn('[OpponentDice] reskin preload failed', err));
        continue;
      }
      this.reskinOpponentSlot(playerId, i, newId);
    }
  }

  /** True while a die is being dragged or is mid-flight — re-skinning then
   *  would teleport it or break the drag group. */
  private isBusyPhase(phase: DicePhase): boolean {
    return phase === DicePhase.COLLECTING || phase === DicePhase.HOLDING
      || phase === DicePhase.THROWN || phase === DicePhase.SETTLING;
  }

  /**
   * Last-chance skin swap at an activation point: any slot still wearing the
   * red fallback whose document IS ready gets re-skinned NOW, before the die
   * becomes visible. Called at the moments a replica first shows (ready-dice,
   * pickup, throw replay) so a player never actually sees the fallback —
   * the docs preload at join, so by first activation they're ready. Forces
   * past the busy-phase guard (safe: dice riding the drag group are still
   * skipped inside reskinOpponentSlot, and held refs are re-pointed).
   */
  private ensureSlotSkins(state: OpponentState): void {
    if (!this.assetManager) return;
    for (let i = 0; i < state.dice.length; i++) {
      const id = state.equippedDice[i];
      if (!id || state.dice[i].asset?.diceId === id) continue;
      if (!this.assetManager.isReady(id)) continue;
      this.reskinOpponentSlot(state.playerId, i, id, true);
    }
  }

  /** Replace one opponent slot's die with the given (ready) skin, PRESERVING
   *  its current transform + scene visibility (a die shown at rest on the felt
   *  stays put with the new skin). Releases the old pooled asset + disposes the
   *  old die first (mirrors removeOpponent), so the pool never double-frees.
   *  `force` bypasses the busy-phase guard for activation-point swaps; a die
   *  currently riding the drag group is never swapped regardless. */
  private reskinOpponentSlot(playerId: string, i: number, diceId: string, force = false): void {
    const state = this.opponents.get(playerId);
    if (!state || !this.assetManager) return;
    if (!force && this.isBusyPhase(state.phase)) return;
    const opponentDie = state.dice[i];
    if (!opponentDie) return;
    // Never swap a die that's parented to the live drag group — the new mesh
    // wouldn't be in the group and the die would visually vanish mid-drag.
    if (state.dragGroup && opponentDie.dice.mesh.parent === state.dragGroup) return;
    if ((opponentDie.asset?.diceId ?? null) === diceId) return;
    if (!this.assetManager.isReady(diceId)) return;

    const asset = this.assetManager.acquireDice(diceId);
    if (!asset) return;

    // Preserve where/how the old die was showing — including its body state:
    // held (ready-dice showcase, no physics) vs settled (live sleeping
    // collider under the HI_LO knockable model — must stay knockable and
    // reconciliation-addressable after the reskin).
    const oldDie = opponentDie.dice;
    const oldMesh = oldDie.mesh;
    const wasActive = opponentDie.active;
    const wasInScene = !!oldMesh.parent;
    const wasHeld = oldDie.held;
    const pos = oldMesh.position.clone();
    const quat = oldMesh.quaternion.clone();

    if (opponentDie.asset) this.assetManager.releaseDice(opponentDie.asset);
    oldDie.dispose();

    const newDie = new Dice(this.engine.physics, this.engine.scene, {
      position: { x: pos.x, y: pos.y, z: pos.z },
      size: 1,
      color: 0xcc0000,
    }, asset);
    newDie.setRotationFromQuaternion({ x: quat.x, y: quat.y, z: quat.z, w: quat.w });
    if (wasHeld) {
      newDie.pickup(); // held mode (no physics) — ready-dice showcase
    } else {
      // Settled live collider: body at pose, asleep. sleep() zeroes
      // velocities; full mask stays so the die remains knockable.
      newDie.body.sleep();
    }
    if (wasActive && wasInScene) {
      if (!newDie.mesh.parent) this.engine.scene.add(newDie.mesh);
      if (wasHeld) {
        // Held dice skip syncToPhysics, so a mesh-only tween is safe: fake
        // the fall by dropping the new skin in from slightly above its spot.
        const DROP_HEIGHT = 1.4;
        newDie.mesh.position.y = pos.y + DROP_HEIGHT;
        gsap.to(newDie.mesh.position, {
          y: pos.y, duration: 0.4, ease: 'bounce.out',
          onUpdate: () => renderScheduler.markShadowsDirty(),
        });
      }
      // Settled dice appear in place — syncToPhysics owns their mesh, so a
      // mesh tween would fight it every frame.
    } else {
      this.engine.scene.remove(newDie.mesh);
    }
    void prewarmShaders(newDie.mesh, this.engine.camera, this.engine.scene);
    opponentDie.dice = newDie;
    opponentDie.asset = asset;
    opponentDie.active = wasActive;
    // If the old die was tracked as held (activation-point swap right before
    // a throw replay releases it), re-point the ref so handleThrow's safety
    // net never touches the disposed die.
    const heldIdx = state.heldDice.indexOf(oldDie);
    if (heldIdx >= 0) state.heldDice[heldIdx] = newDie;
  }

  /**
   * Create a billboard label for showing player info during drag.
   * Uses TSS: avatar circle (left) + username (right)
   */
  /** Drag/ready label tree (avatar circle + name). */
  private dragLabelTree(playerInfo: PlayerInfo): UINodeConfig {
    return {
      tag: 'root',
      style: {
        direction: 'row',
        alignItems: 'center',
        padding: [10, 14, 10, 10],
        gap: 12,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderRadius: 12,
        borderColor: 'rgba(255, 215, 0, 0.6)',
        borderWidth: 2,
      },
      children: [
        {
          tag: 'avatar',
          style: { color: '#FFFFFF', fontFamily: 'sans-serif' },
          circle: {
            radius: 22,
            fill: 'rgba(255, 215, 0, 0.15)',
            stroke: 'rgba(255, 215, 0, 0.6)',
            strokeWidth: 2,
            ...this.avatarCircle(playerInfo),
            textSize: 24,
          },
        },
        {
          tag: 'name',
          text: playerInfo.username,
          style: {
            fontWeight: 'bold',
            fontSize: 20,
            fontFamily: 'Oswald, sans-serif',
            color: '#FFD700',
            maxWidth: 180,
            overflow: 'ellipsis',
          },
        },
      ],
    };
  }

  private createDragLabel(playerInfo: PlayerInfo): TSS {
    return new TSS(
      this.dragLabelTree(playerInfo),
      {
        worldHeight: 0.625,
        mode: 'sprite',
        // 2× DPI for crisp text on retina (see resultLabel note). Cheap — small label.
        resolution: 2,
        depthTest: false,
        depthWrite: false,
      }
    );
  }

  /**
   * Show an opponent's dice at their rest positions with a name label above,
   * before they start dragging. Mirrors the local player's "PICK UP DICE" layout
   * but replaces per-die pickup tags with a single player identity pill.
   *
   * Called when it becomes the opponent's turn to roll (BANKER_ROLL / PLAYER_ROLL).
   * Transitions to drag group automatically on first dice:sync (handleDiceSync
   * disposes the ready label before setupDragGroup runs).
   */
  showReadyDice(playerId: string, positions: Array<{ x: number; y: number; z: number }>): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);
    if (!state) {
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    // If opponent is already dragging or past drag (thrown/stopped), don't
    // retroactively show a ready label — that would conflict with the drag group.
    if (state.dragGroup || state.heldDice.length > 0) return;
    if (state.phase === DicePhase.THROWN || state.phase === DicePhase.STOPPED) return;

    const count = Math.min(positions.length, state.dice.length);

    // Dice are about to become visible — apply any ready-but-unapplied skin
    // first so the fallback red box is never actually shown.
    this.ensureSlotSkins(state);

    // Activate the first `count` dice at the given world positions (in scene root,
    // not a group — they sit at rest, no lerp target).
    for (let i = 0; i < count; i++) {
      const opponentDie = state.dice[i];
      const pos = positions[i];
      opponentDie.dice.setPosition(pos);
      // Reset rotation to identity so dice sit flat — a die carrying rotation
      // from its previous landed pose would appear tilted against its neighbors.
      opponentDie.dice.setRotationFromQuaternion({ x: 0, y: 0, z: 0, w: 1 });
      if (!opponentDie.active) {
        this.engine.scene.add(opponentDie.dice.mesh);
        opponentDie.active = true;
      }
      // Keep physics disabled — dice sit still until opponent picks them up.
      opponentDie.dice.pickup();
    }

    // Reuse existing label if present (phase re-entry shouldn't recreate)
    if (state.readyLabel) {
      this.positionReadyLabel(state, positions);
      state.readyLabel.object3D.visible = true;
      return;
    }

    const label = this.createDragLabel(state.playerInfo);
    label.render();
    this.engine.scene.add(label.object3D);
    state.readyLabel = label;
    this.positionReadyLabel(state, positions);
  }

  /** Position the ready label above the geometric centroid of the provided positions. */
  private positionReadyLabel(state: OpponentState, positions: Array<{ x: number; y: number; z: number }>): void {
    if (!state.readyLabel || positions.length === 0) return;
    // Geometric mean of positions — for 2 dice this lands the label
    // between them (matching the dragGroup label which sits at the
    // group origin = midpoint). Picking positions[Math.floor(n/2)]
    // worked for 3 dice (middle die IS the center) but for 2 dice it
    // returned the right die, leaving the label visibly off-center.
    let cx = 0, cy = 0, cz = 0;
    for (const p of positions) { cx += p.x; cy += p.y; cz += p.z; }
    cx /= positions.length; cy /= positions.length; cz /= positions.length;
    state.readyLabel.object3D.position.set(cx, cy + 1.8, cz);
  }

  /** Hide and dispose the ready label + deactivate ready-state dice */
  hideReadyDice(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state) return;
    if (state.readyLabel) {
      state.readyLabel.object3D.removeFromParent();
      state.readyLabel.dispose();
      state.readyLabel = null;
    }
  }

  /** Height above the table the post-drop identity pill floats at. The dropped
   *  dice fall ~straight down and rest near table level, so a fixed world Y
   *  keeps the pill a consistent gap above them (matches the ~1.8 gap the
   *  ready-dice pill uses over its y:1 rest dice). */
  private static readonly DROP_LABEL_HEIGHT = 2.3;

  /**
   * After an opponent DROPS their held dice (no throw), keep the identity pill
   * floating above the now-resting dice — the spectator equivalent of the
   * shooter's "PICK UP DICE" affordance: whose dice are down, about to roll
   * again. Clears any stale drag/ready/result label first so exactly ONE pill
   * remains, parked over the centroid of where the dice dropped (a near-vertical
   * drop lands at the same X/Z). Reuses the readyLabel slot so the next pickup
   * disposes it through the normal handleDiceSync path.
   */
  showDropIdentityLabel(playerId: string, positions: Array<{ x: number; y: number; z: number }>): void {
    if (playerId === this.localPlayerId) return;
    const state = this.opponents.get(playerId);
    if (!state || positions.length === 0) return;

    // Clear any label that could linger (drag label is normally disposed by
    // handleThrow's cleanupDragGroup, but be defensive) so we never stack two.
    if (state.dragLabel) { state.dragLabel.object3D.removeFromParent(); state.dragLabel.dispose(); state.dragLabel = null; }
    if (state.resultLabel) { state.resultLabel.object3D.removeFromParent(); state.resultLabel.dispose(); state.resultLabel = null; }
    if (state.readyLabel) { state.readyLabel.object3D.removeFromParent(); state.readyLabel.dispose(); state.readyLabel = null; }

    let cx = 0, cz = 0;
    for (const p of positions) { cx += p.x; cz += p.z; }
    cx /= positions.length; cz /= positions.length;

    const label = this.createDragLabel(state.playerInfo);
    label.render();
    label.object3D.position.set(cx, OpponentDiceManager.DROP_LABEL_HEIGHT, cz);
    this.engine.scene.add(label.object3D);
    state.readyLabel = label;
  }

  /**
   * Set up drag group for an opponent (called when dragging starts)
   * Creates a group that holds both dice meshes and label - only this group is lerped
   */
  private setupDragGroup(state: OpponentState): void {
    if (state.dragGroup) return; // Already set up

    // Create main drag group - this is the only thing we lerp
    state.dragGroup = new THREE.Group();
    this.engine.scene.add(state.dragGroup);

    // Create and add the label
    state.dragLabel = this.createDragLabel(state.playerInfo);
    state.dragLabel.object3D.position.set(0, 2, 0); // Position above dice (relative to group)
    state.dragGroup.add(state.dragLabel.object3D);

  }

  /**
   * Add dice meshes to the drag group with proper offsets
   * Dice are parented to the group so they move together
   */
  private addDiceToDragGroup(state: OpponentState): void {
    if (!state.dragGroup) return;

    for (let i = 0; i < state.heldDice.length; i++) {
      const die = state.heldDice[i];
      const offsetX = (i - (state.heldDice.length - 1) / 2) * this.HOLD_SPACING;

      // Remove from scene root (if present)
      this.engine.scene.remove(die.mesh);

      // Add to drag group with offset position
      state.dragGroup.add(die.mesh);
      die.mesh.position.set(offsetX, 0, 0);
    }
  }

  /**
   * Remove dice meshes from drag group back to scene root (for physics simulation).
   * World transforms are preserved through the reparent (mesh + body + physics
   * interpolation). handleThrow overwrites them with authoritative values for
   * every die covered by throwParams — but a die NOT covered (version-skewed
   * partial payload) must keep its hand pose, not inherit its drag-group-LOCAL
   * offset as world coordinates (that parked dice at the world origin — the
   * "stuck dice" bug).
   */
  private removeDiceFromDragGroup(state: OpponentState): void {
    if (!state.dragGroup) return;

    const worldPos = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    for (const die of state.heldDice) {
      die.mesh.getWorldPosition(worldPos);
      die.mesh.getWorldQuaternion(worldQuat);
      state.dragGroup.remove(die.mesh);
      this.engine.scene.add(die.mesh);
      die.setPosition({ x: worldPos.x, y: worldPos.y, z: worldPos.z });
      die.setRotationFromQuaternion({ x: worldQuat.x, y: worldQuat.y, z: worldQuat.z, w: worldQuat.w });
    }
  }

  /**
   * Clean up drag group (called when dragging ends)
   */
  private cleanupDragGroup(state: OpponentState): void {
    // First remove dice from group back to scene
    this.removeDiceFromDragGroup(state);

    // Dispose label
    if (state.dragLabel) {
      state.dragLabel.dispose();
      state.dragLabel = null;
    }

    // Remove group from scene
    if (state.dragGroup) {
      this.engine.scene.remove(state.dragGroup);
      state.dragGroup = null;
    }

  }

  /**
   * Deactivate all dice for this opponent (remove from scene)
   */
  private deactivateAllDice(state: OpponentState): void {
    // Clean up drag group first (removes dice from group, disposes label)
    this.cleanupDragGroup(state);

    // Dispose any ready-state label (opponent never dragged before transition)
    if (state.readyLabel) {
      state.readyLabel.object3D.removeFromParent();
      state.readyLabel.dispose();
      state.readyLabel = null;
    }


    for (const opponentDie of state.dice) {
      if (opponentDie.active) {
        this.engine.scene.remove(opponentDie.dice.mesh);
        opponentDie.dice.pickup();
        opponentDie.active = false;
      }
    }
    state.heldDice = [];
    // Drop any buffered drag snapshots — otherwise a re-pickup next round
    // would briefly interpolate from the previous round's stale positions.
    state.dragBuffer.length = 0;
  }

  /**
   * Handle dice sync during drag - pick up dice from pool and sync position
   * Only show dice if server has confirmed opponent is COLLECTING or HOLDING
   */
  handleDiceSync(data: DiceSyncPayload): void {
    for (const playerData of data.players) {
      // Skip local player
      if (playerData.playerId === this.localPlayerId) continue;

      let state = this.opponents.get(playerData.playerId);

      // Auto-load opponent if not exists
      if (!state) {
        this.loadOpponent(playerData.playerId);
        state = this.opponents.get(playerData.playerId)!;
      }

      // Only show dice if server has confirmed opponent is actively collecting/holding
      if (state.phase !== DicePhase.COLLECTING && state.phase !== DicePhase.HOLDING) {
        continue;
      }

      // Sequence check to avoid out-of-order/stale updates
      if (playerData.seq !== undefined && playerData.seq <= state.lastDragSeq) {
        continue;
      }

      state.lastDragSeq = playerData.seq ?? state.lastDragSeq;
      // LOCAL arrival time — the stale-drag check (update loop) compares this
      // against Date.now(). Using the opponent's playerData.timestamp here mixed
      // two device clocks: with any skew, Date.now() − theirTimestamp could sit
      // near STALE_TIMEOUT_MS and toggle as each sync landed → dice flicker at the
      // sync rate, and vanish (instead of throwing) when the drag stopped.
      state.lastDragTimestamp = Date.now();

      // Push to interpolation buffer with LOCAL arrival time. Update loop
      // interpolates between snapshots at renderTime = now - INTERPOLATION_DELAY_MS.
      // Trim snapshots older than 2× delay so the buffer never grows unbounded.
      const arrivalMs = Date.now();
      state.dragBuffer.push({
        position: { x: playerData.position.x, y: playerData.position.y, z: playerData.position.z },
        time: arrivalMs,
      });
      const trimThreshold = arrivalMs - 2 * OpponentDragConfig.interpolationDelayMs;
      while (state.dragBuffer.length > 2 && state.dragBuffer[0].time < trimThreshold) {
        state.dragBuffer.shift();
      }

      // Keep targetPosition as the latest known target (used as fallback when
      // renderTime exceeds the newest buffered snapshot, e.g. opponent stopped).
      state.targetPosition.set(
        playerData.position.x,
        playerData.position.y,
        playerData.position.z
      );
      state.targetRotation.set(
        playerData.rotation.x,
        playerData.rotation.y,
        playerData.rotation.z,
        playerData.rotation.w
      );

      // Pick up all active dice during collection
      // For BANKER_ROLL/PLAYER_ROLL this is 3 dice, for HI_LO this is 1 die
      if (state.heldDice.length < state.activeDiceCount) {
        const isFirstPickup = state.heldDice.length === 0;

        // Dice are about to enter the drag group (become visible) — apply any
        // ready-but-unapplied skin NOW; once a die rides the group it can't be
        // swapped until the throw.
        this.ensureSlotSkins(state);

        // Set up drag group on first pickup (creates group + label)
        if (isFirstPickup) {
          // Dispose any ready-state label — drag group owns the identity pill now
          if (state.readyLabel) {
            state.readyLabel.object3D.removeFromParent();
            state.readyLabel.dispose();
            state.readyLabel = null;
          }
          // Dispose any HI_LO result label parked at the previous stopped die
          // position — the drag label that setupDragGroup creates follows the
          // dice being grabbed. Leaving the result label stranded is the visual
          // bug where the player's name hovers above the old dice location.
          if (state.resultLabel) {
            state.resultLabel.object3D.removeFromParent();
            state.resultLabel.dispose();
            state.resultLabel = null;
          }
          this.setupDragGroup(state);
          // Position group at target
          if (state.dragGroup) {
            state.dragGroup.position.copy(state.targetPosition);
          }
        }

        // Activate and pick up all active dice
        for (let i = 0; i < state.activeDiceCount && i < state.dice.length; i++) {
          const opponentDie = state.dice[i];
          if (!state.heldDice.includes(opponentDie.dice)) {
            opponentDie.active = true;
            opponentDie.dice.pickup();
            state.heldDice.push(opponentDie.dice);
          }
        }

        // Add dice to drag group (positions them relative to group center)
        if (isFirstPickup) {
          this.addDiceToDragGroup(state);
        }
      }
    }
  }

  /**
   * Handle opponent collecting more dice
   */
  handleDicePickup(playerId: string, diceIndex: number): void {
    if (playerId === this.localPlayerId) return;

    const state = this.opponents.get(playerId);
    if (!state) return;

    if (diceIndex < state.dice.length && diceIndex < state.activeDiceCount) {
      // About to show this die — apply any ready-but-unapplied skin first.
      this.ensureSlotSkins(state);
      const opponentDie = state.dice[diceIndex];
      if (!state.heldDice.includes(opponentDie.dice)) {
        if (!opponentDie.active) {
          this.engine.scene.add(opponentDie.dice.mesh);
          opponentDie.active = true;
        }
        opponentDie.dice.pickup();
        state.heldDice.push(opponentDie.dice);
        // Quieter than the local pickup — another player's collecting reads
        // as background table noise, not your own action.
        sound.play(SoundId.DICE_PICKUP, { volume: 0.6 });
      }
    }
  }

  /**
   * Handle opponent throw - apply physics to the active dice
   */
  handleThrow(playerId: string, throwParams: DiceThrowParams): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);

    // Auto-load if not exists
    if (!state) {
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    const diceCount = throwParams.diceStates.length;

    // Update active dice count to match throw
    state.activeDiceCount = diceCount;

    // Clean up drag group first - removes dice from group back to scene root
    this.cleanupDragGroup(state);

    // Last chance before the replay becomes visible: apply any
    // ready-but-unapplied skin. Dice are back in the scene root (out of the
    // drag group), so the swap is safe; held refs are re-pointed inside, and
    // the physics reset below overwrites the pose either way.
    this.ensureSlotSkins(state);

    // Ensure all dice being thrown are active and in scene root
    for (let i = 0; i < diceCount && i < state.dice.length; i++) {
      const opponentDie = state.dice[i];

      // Add to scene if not already there
      if (!opponentDie.dice.mesh.parent || opponentDie.dice.mesh.parent !== this.engine.scene) {
        this.engine.scene.add(opponentDie.dice.mesh);
      }
      opponentDie.active = true;
    }

    // Apply physics to all dice being thrown
    const hePerDie = throwParams.halfExtentsPerDie;
    const heLegacy = throwParams.halfExtents;
    for (let i = 0; i < diceCount && i < state.dice.length; i++) {
      const diceState = throwParams.diceStates[i];
      const die = state.dice[i].dice;

      // Match the shooter's collider shape BEFORE loading the throw so this
      // client simulates the same bounces. Per-die extents (index-aligned with
      // diceStates) cover mixed-skin shooters and shooter-side fallback cubes;
      // the legacy single value (shooter's dice[0]) is the version-skew
      // fallback. No-op when it already matches (e.g. cube dice).
      const he = hePerDie?.[i] ?? heLegacy;
      if (he) die.setPhysicsHalfExtents(he);

      // A stale final-state correction tween writing mesh + body pose into
      // this new flight would fork the replay — kill it before the reset.
      this.killFinalStateTween(die);

      // Fully reset physics state
      die.body.velocity.setZero();
      die.body.angularVelocity.setZero();
      die.body.force.setZero();
      die.body.torque.setZero();

      // Reset mesh transform (clear any local offsets from drag group)
      die.mesh.position.set(0, 0, 0);
      die.mesh.quaternion.identity();
      die.mesh.updateMatrixWorld(true);

      // Route through setPosition / setRotationFromQuaternion so the physics
      // interpolation state is refreshed alongside the body + mesh — otherwise
      // the first render frame after release lerps from stale pose to throw pose.
      die.setPosition(diceState.position);
      die.setRotationFromQuaternion(diceState.rotation);
      die.setVelocity(diceState.velocity);
      die.setAngularVelocity(diceState.angularVelocity);

      // Release - enables physics
      die.release();
    }

    // Safety net: any die we were holding that throwParams did NOT cover
    // (drops always cover all dice now, but tolerate a version-skewed shooter
    // sending a partial payload) must never stay frozen in pickup mode. Release
    // it at its preserved hand pose with zero velocity so it just falls and
    // settles like the rest.
    const covered = new Set<Dice>();
    for (let i = 0; i < diceCount && i < state.dice.length; i++) {
      covered.add(state.dice[i].dice);
    }
    for (const die of state.heldDice) {
      if (covered.has(die)) continue;
      die.body.velocity.setZero();
      die.body.angularVelocity.setZero();
      die.release();
    }

    // Clear held dice + drag buffer after throw (they're now rolling).
    // Stale snapshots would otherwise leak into the next pickup's interpolation
    // and pull dice toward the previous round's final drag pose.
    state.heldDice = [];
    state.dragBuffer.length = 0;
  }

  /**
   * Update opponent dice (call every frame)
   * 
   * Performance: With 5 opponents × 3 dice, we only do 5 position lerps (one per drag group)
   * instead of 15 individual dice lerps + 5 label lerps
   * 
   * Design: During drag, only position needs to match the opponent.
   * - Label stays upright (billboard, always faces camera)
   * - Dice rotate independently on client (cosmetic tumble effect)
   * - Actual rotation only matters at throw time (from throwParams)
   */
  update(deltaTime: number): void {
    for (const state of this.opponents.values()) {
      // Only lerp the drag group POSITION - dice are children so they move automatically
      // This is O(n) for n opponents instead of O(n*dice) for individual lerps
      // NOTE: We do NOT rotate the group - label should stay upright
      if (state.dragGroup && state.heldDice.length > 0) {
        // Opponent is dragging (network-driven, not local input/physics/gsap) —
        // keep the renderer at full rate so their dice move smoothly instead of
        // stuttering at the idle heartbeat. Their dice cast shadows, so the
        // shadow map must follow too.
        renderScheduler.pulse();
        renderScheduler.markShadowsDirty();

        // Client-side interpolation: render the drag pose at "now - delay"
        // by interpolating between the two buffered snapshots that bracket
        // that timestamp. Smooth regardless of packet timing jitter.
        const renderTime = Date.now() - OpponentDragConfig.interpolationDelayMs;
        const buf = state.dragBuffer;
        const newest = buf.length > 0 ? buf[buf.length - 1] : null;
        const haveBracket = buf.length >= 2 && renderTime >= buf[0].time && newest !== null && renderTime <= newest.time;

        if (haveBracket) {
          // Find the snapshot pair [from, to] such that from.time <= renderTime <= to.time.
          // Linear scan from oldest is fine — buffer holds 3-6 entries typically.
          let fromIdx = 0;
          for (let i = 0; i < buf.length - 1; i++) {
            if (buf[i].time <= renderTime && buf[i + 1].time >= renderTime) {
              fromIdx = i;
              break;
            }
          }
          const from = buf[fromIdx];
          const to = buf[fromIdx + 1];
          const span = to.time - from.time;
          const alpha = span > 0 ? Math.max(0, Math.min(1, (renderTime - from.time) / span)) : 1;
          state.dragGroup.position.set(
            from.position.x + (to.position.x - from.position.x) * alpha,
            from.position.y + (to.position.y - from.position.y) * alpha,
            from.position.z + (to.position.z - from.position.z) * alpha,
          );
        } else if (buf.length > 0) {
          // No bracketing pair (renderTime past newest, or buffer just primed).
          // Exponentially follow the latest known target so we don't freeze
          // and then jump when the next packet lands.
          const k = 1 - Math.exp(-OpponentDragConfig.tailFollowRate * deltaTime);
          const p = state.dragGroup.position;
          p.x += (state.targetPosition.x - p.x) * k;
          p.y += (state.targetPosition.y - p.y) * k;
          p.z += (state.targetPosition.z - p.z) * k;
        }

        // Rotate dice individually for a nice tumble effect (purely cosmetic)
        // This is cheap - just incrementing rotation, no lerp target needed
        for (const die of state.heldDice) {
          die.mesh.rotation.x += deltaTime * 1.5;
          die.mesh.rotation.y += deltaTime * 2.0;
          die.mesh.getWorldPosition(_worldPosition);
          die.body.position.set(_worldPosition.x, _worldPosition.y, _worldPosition.z);
        }
      }

      // NOTE: previously hid the dice if no drag update arrived within
      // STALE_TIMEOUT_MS — but a stationary HOLD sends no position updates (nothing
      // moved), so a legitimately-held die vanished after 800ms. The relay can't
      // distinguish "holding still" from "connection died", and hiding a held die
      // (common) is far worse than a rare frozen die from a dropped connection
      // (which self-heals on throw / leave / round reset). So held dice now persist
      // at their last pose until the opponent actually throws or leaves.

      // Sync rolling dice (active but not held) to physics
      // These are dice that have been thrown and are simulating. Settled
      // replicas deliberately stay live mass-1 sleeping colliders during
      // HI_LO — a later contender's throw physically knocks them (with
      // gravity) on every sim, server included (HiLoPhysicsWorld.settleDie
      // keeps the twin body). Non-HI_LO parking is owned by applyFinalStates
      // and loadShooter/parkAllExcept.
      for (const opponentDie of state.dice) {
        if (opponentDie.active && !state.heldDice.includes(opponentDie.dice) && !opponentDie.dice.held) {
          opponentDie.dice.syncToPhysics(deltaTime);
        }
      }

      // Edge-stuck dice are corrected by the shooter's authoritative finalStates
      // (sent with DicePhase.STOPPED via applyFinalStates). No local nudge here —
      // nudging during THROWN phase causes HI_LO physics divergence because each
      // client's die may appear to rest at slightly different times.
    }
  }

  /**
   * Apply shooter-authoritative final dice poses to correct any local divergence
   * (e.g. edge-stuck die that our local physics settled differently than the shooter).
   * Called on DICE_PHASE STOPPED with the shooter's post-nudge pose snapshot.
   */
  applyFinalStates(
    playerId: string,
    finalStates: Array<{
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
    }>,
    opts: { park?: boolean } = {},
  ): void {
    if (playerId === this.localPlayerId) return;
    const state = this.opponents.get(playerId);
    if (!state) return;

    const count = Math.min(finalStates.length, state.activeDiceCount, state.dice.length);
    for (let i = 0; i < count; i++) {
      const pose = finalStates[i];
      const opponentDie = state.dice[i];
      if (!opponentDie.active) continue;
      const die = opponentDie.dice;

      if (opts.park === false) {
        // HI_LO: settle as a LIVE sleeping collider — a later contender's
        // throw physically knocks it (with gravity, mass stays 1), matching
        // the server world which keeps the twin body (settleDie). sleep()
        // zeroes velocities and stops integration so the glide owns the pose
        // until something actually collides with it.
        die.body.force.setZero();
        die.body.torque.setZero();
        die.body.sleep();
      } else {
        // Non-HI_LO: park (mass 0 + mask NONE + sleep) so the glide owns it
        // and the next roller's in-flight dice pass straight through — the
        // single-shooter clean-world model. parkAtRest (not an inline mass=0)
        // so updateMassProperties runs: a bare `mass = 0` leaves invMass at 1,
        // an inconsistent body state. handleThrow's release() restores full
        // collision if this opponent rolls again.
        die.parkAtRest();
      }

      // Lerp position + rotation toward server-authoritative pose instead
      // of snapping. Small divergences (typical) become imperceptible;
      // large divergences (rare) become a brief glide rather than a teleport.
      const fromPos = die.mesh.position.clone();
      const targetPos = new THREE.Vector3(pose.position.x, pose.position.y, pose.position.z);
      const fromQuat = die.mesh.quaternion.clone();
      const targetQuat = new THREE.Quaternion(pose.quaternion.x, pose.quaternion.y, pose.quaternion.z, pose.quaternion.w);

      const tmpQuat = new THREE.Quaternion();
      const progress = { t: 0 };
      // Kill any previous correction still gliding this die (killTweensOf on
      // the fresh progress object matched nothing — each call makes a new one).
      this.killFinalStateTween(die);
      const tween = gsap.to(progress, {
        t: 1,
        duration: 0.25,
        ease: 'power2.out',
        onUpdate: () => {
          renderScheduler.markShadowsDirty();
          die.mesh.position.lerpVectors(fromPos, targetPos, progress.t);
          tmpQuat.copy(fromQuat).slerp(targetQuat, progress.t);
          die.mesh.quaternion.copy(tmpQuat);
          // Mirror onto the body so physics state stays consistent if anything
          // queries it mid-lerp.
          die.body.position.set(die.mesh.position.x, die.mesh.position.y, die.mesh.position.z);
          die.body.quaternion.set(tmpQuat.x, tmpQuat.y, tmpQuat.z, tmpQuat.w);
        },
        onComplete: () => {
          this.finalStateTweens.delete(die);
        },
      });
      this.finalStateTweens.set(die, tween);
    }
  }

  /**
   * Reset opponent dice (after roll complete) - deactivate all dice
   */
  resetOpponent(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state) return;


    this.deactivateAllDice(state);
    this.hideResultLabel(playerId);
    state.phase = DicePhase.IDLE;
  }

  /**
   * Reset all opponent dice - useful when transitioning to new game phase
   */
  resetAllOpponents(): void {
    for (const playerId of this.opponents.keys()) {
      this.resetOpponent(playerId);
    }
  }

  /**
   * Remove opponent completely (when they leave the game)
   */
  removeOpponent(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (state) {
      // Clean up drag group first (removes dice from group, disposes label)
      this.cleanupDragGroup(state);

      for (const opponentDie of state.dice) {
        // Release asset back to pool before disposing
        if (opponentDie.asset && this.assetManager) {
          this.assetManager.releaseDice(opponentDie.asset);
        }
        opponentDie.dice.dispose();
      }
      this.opponents.delete(playerId);
    }
  }

  /**
   * Clear all dice for a specific player (alias for removeOpponent)
   */
  clearPlayer(playerId: string): void {
    this.removeOpponent(playerId);
  }

  /**
   * Tear down any opponent whose playerId is no longer in the active roster.
   * Driven off the reconciled player list each game:update — when the server
   * prunes a deliberate leaver they drop out of update.players, and their dice,
   * physics bodies, pooled assets, and labels must be released or they leak in
   * the scene and the stale collider can hit the next roller's dice. Note:
   * disconnected-but-seated players (membership kept for reconnect) STAY in
   * update.players, so they are intentionally NOT torn down here.
   */
  removeOpponentsExcept(presentPlayerIds: string[]): void {
    const present = new Set(presentPlayerIds);
    for (const playerId of [...this.opponents.keys()]) {
      if (present.has(playerId)) continue;
      // Never tear down an opponent who is mid-action. A transient / partial
      // update that momentarily drops them from the roster must NOT yank their
      // dice out from under the live drag/throw relay — doing so destroys their
      // dragGroup and the next update re-creates it, which flickers the dice. A
      // genuine deliberate-leaver isn't dragging/throwing, so they still get
      // cleaned up; an actively-interacting player just defers to a later update.
      const state = this.opponents.get(playerId);
      if (state && (state.dragGroup || state.heldDice.length > 0
          || state.phase === DicePhase.THROWN || state.phase === DicePhase.STOPPED)) {
        continue;
      }
      this.removeOpponent(playerId);
    }
  }

  /**
   * Freeze every opponent's dice EXCEPT the given shooter, so only the active
   * shooter's dice are live colliders in the shared world (Phone Dice loadPlayer
   * model). Settled dice are already parked by applyFinalStates; this also
   * catches a PREVIOUS roller whose STOPPED hasn't landed yet — the spectator
   * freezes them where they are rather than letting the new throw graze them and
   * fork the sim. Visual state (mesh, result labels, active flag) is untouched;
   * parkAtRest only flips the body to mass 0 / mask NONE. Idempotent.
   */
  parkAllExcept(shooterId: string | null): void {
    for (const [playerId, state] of this.opponents) {
      if (playerId === shooterId) continue;
      for (const od of state.dice) {
        if (od.active) od.dice.parkAtRest();
      }
    }
  }

  /**
   * Clear all opponent dice
   */
  clear(): void {
    for (const playerId of this.opponents.keys()) {
      this.removeOpponent(playerId);
    }
  }

  /**
   * Check if an opponent is loaded
   */
  hasOpponent(playerId: string): boolean {
    return this.opponents.has(playerId);
  }

  /**
   * Get opponent's active dice count
   */
  getOpponentDiceCount(playerId: string): number {
    return this.opponents.get(playerId)?.activeDiceCount ?? 0;
  }

  dispose(): void {
    OpponentDiceManager.avatarLoadListeners.delete(this.redrawLabels);
    this.clear();
  }
}
